import { useUserStore } from "@/stores";
import { predspendCache } from "@/utils/predspend-cache";
import { useEffect, useRef } from "react";

/**
 * Hook to manage predspend cache lifecycle
 * Handles cache invalidation when organization changes and provides predspend-specific cache utilities
 */
export const usePredspendCache = () => {
	const user = useUserStore((state) => state.user);
	const previousOrgId = useRef<number | null>(null);

	useEffect(() => {
		const currentOrgId = user?.organization?.id || user?.organization_id;

		// If organization changed, invalidate all predspend caches
		if (previousOrgId.current && previousOrgId.current !== currentOrgId) {
			console.log(
				`Organization changed from ${previousOrgId.current} to ${currentOrgId}, clearing predspend caches`,
			);
			predspendCache.clear();
		}

		// Update the previous org ID
		previousOrgId.current = currentOrgId || null;
	}, [user?.organization?.id, user?.organization_id]);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			// Don't clear cache on unmount, as it should persist across navigation
			// Only clear on organization change or explicit actions
		};
	}, []);

	return {
		/**
		 * Clear all predspend cached data
		 */
		clearCache: predspendCache.clear.bind(predspendCache),

		/**
		 * Clear cache for current organization
		 */
		clearOrganizationCache: () => {
			const currentOrgId = user?.organization?.id || user?.organization_id;
			if (currentOrgId) {
				predspendCache.invalidateOrganization(currentOrgId);
			}
		},

		/**
		 * Clear cache for a specific replacement strategy
		 */
		clearStrategyCache: (strategy: string) => {
			const currentOrgId = user?.organization?.id || user?.organization_id;
			if (currentOrgId) {
				predspendCache.invalidateStrategy(currentOrgId, strategy);
			}
		},

		/**
		 * Get predspend cache statistics
		 */
		getCacheStats: predspendCache.getStats.bind(predspendCache),

		/**
		 * Get current organization ID for cache operations
		 */
		getCurrentOrgId: () => user?.organization?.id || user?.organization_id || 0,
	};
};
